import { useEffect, useMemo, useState } from "react";
import LineChartCard from "components/Charts/LineChartCard";
import { getGreenhouseTelemetrySeries, getGreenhouseLevelSensorSeries } from "services/greenhouseApi";
import { getTelemetry, getTelemetrySeries } from "services/api";
import { mergeTimeSeries } from "utils/timeSeries";
import { countLabelled, withTruthRows } from "utils/groundTruth";
import { useStableState } from "utils/stableState";
import {
  TELEMETRY_GROUPS,
  KEY_LABELS,
  deriveNumericFields,
  seriesRequestFields,
  withDerivedSeries,
} from "utils/telemetryFields";

// Greenhouse controller telemetry-series fields (see greenhouse_views.py's
// greenhouse_telemetry_series) - a small fixed set, unlike the PadiHAT side
// which offers whatever that device actually publishes.
const GREENHOUSE_FIELDS = [
  "target_temp_c",
  "current_temp_c",
  "target_rh_pct",
  "current_rh_pct",
  "step_state",
];

const GREENHOUSE_LABELS = {
  target_temp_c: "Target temp (°C)",
  current_temp_c: "Chamber temp (°C)",
  target_rh_pct: "Target RH (%)",
  current_rh_pct: "Chamber RH (%)",
  step_state: "Step state",
};

// KIT0139 pressure/level sensor (see greenhouse_level_sensor_series) - same
// small-fixed-set shape as GREENHOUSE_FIELDS above, not derived from a
// raw_payload like PadiHAT's fields: this sensor's own MQTT payload only
// ever has voltage_v/depth_mm, and only the calibrated depth (converted to
// cm, matching the fixed "Water level" chart's
// pressure_level_cm) is meaningful to plot here.
const LEVEL_SENSOR_FIELDS = ["pressure_level_cm"];

const LEVEL_SENSOR_LABELS = {
  pressure_level_cm: "Water level — pressure (cm)",
};

// PadiHAT's water LEVEL is derived by the backend (offset - calibrated distance, ground = 0), so it is
// not a key of the device's raw payload and never shows up in the discovered fields below. d1_cal, which
// does, is the DISTANCE from the sensor to the water, not a level.
// water_level_est_cm is the same level from our own range estimate (d1_est, a distance like d1): the
// backend subtracts it from the same offset, so the two lines share a scale. Offered only for a device
// that reports d1_est (a field node on firmware .62 or later).
const WATER_LEVEL_FIELD = "water_level_cm";
const WATER_LEVEL_EST_FIELD = "water_level_est_cm";
const WATER_LEVEL_LABELS = {
  [WATER_LEVEL_FIELD]: "Water level (cm)",
  [WATER_LEVEL_EST_FIELD]: "Water level, estimated (cm)",
};

// The level a person read off labelled camera photos (see CameraImageModal) - not a series the server
// returns but built from the run's photos, like the fixed chart's red truth line.
const CAMERA_FIELDS = ["truth_level_cm"];
const CAMERA_LABELS = { truth_level_cm: "True level - from photos (cm)" };

const LINE_COLORS = [
  "#2563eb", "#dc2626", "#16a34a", "#ea580c",
  "#9333ea", "#0891b2", "#ca8a04", "#db2777",
];

// Phase 2 default (supersedes Phase 1's "nothing ticked"): the operator's
// explicit choice of "water level detected by PadiHAT, greenhouse temp,
// greenhouse humidity" - actual readings, not targets. d1_cal matches the
// field the fixed "Water level" chart already uses, for
// consistency. Only ever used as a FALLBACK when localStorage has nothing
// yet for this controller+water-device pair.
const DEFAULT_GREENHOUSE_FIELDS = ["current_temp_c", "current_rh_pct"];
const DEFAULT_WATER_FIELDS = [WATER_LEVEL_FIELD];
const DEFAULT_CAMERA_FIELDS = ["truth_level_cm"];
// Ticked by default like d1_cal above - it's the same category (an actual
// water-level reading, not a target), and this is exactly the comparison
// this whole feature exists to make easy to see.
const DEFAULT_LEVEL_SENSOR_FIELDS = ["pressure_level_cm"];

// Scoped to both macs (matches useExperiment's STORAGE_KEY pattern) so
// switching between device pairs never leaks one pair's selection into
// another's.
function storageKey(controllerMac, waterDeviceMac) {
  return `padihat_adhoc_fields_${controllerMac || "none"}_${waterDeviceMac || "none"}`;
}

function loadPersistedFields(controllerMac, waterDeviceMac) {
  try {
    const raw = localStorage.getItem(storageKey(controllerMac, waterDeviceMac));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed?.greenhouseFields) && Array.isArray(parsed?.waterFields)) {
      // A value saved before the camera chip existed also predates the water-level one: it plotted
      // d1_cal (a distance) as "the water level". Move it to the real level and tick the camera
      // labels, so nobody has to go looking for the new chips.
      const oldFormat = !Array.isArray(parsed?.cameraFields);
      const waterFields = oldFormat
        ? [...new Set(parsed.waterFields.map((f) => (f === "d1_cal" ? WATER_LEVEL_FIELD : f)))]
        : parsed.waterFields;
      return {
        greenhouseFields: parsed.greenhouseFields,
        waterFields,
        cameraFields: oldFormat ? DEFAULT_CAMERA_FIELDS : parsed.cameraFields,
        // Backward-compatible with values persisted before this field
        // existed - falls back to the default rather than treating an
        // older, otherwise-valid persisted value as corrupt.
        levelSensorFields: Array.isArray(parsed?.levelSensorFields)
          ? parsed.levelSensorFields
          : DEFAULT_LEVEL_SENSOR_FIELDS,
      };
    }
  } catch {
    // Corrupt/foreign localStorage value under this key - fall through to
    // the default rather than throwing.
  }
  return null;
}

function FieldChip({ label, active, color, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-[11px] font-mono px-2 py-0.5 rounded border transition-colors ${
        active ? "text-white border-transparent" : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100"
      }`}
      style={active ? { backgroundColor: color } : {}}
    >
      {label}
    </button>
  );
}

/**
 * Ad-hoc telemetry panel: lets the operator tick ANY field either device
 * publishes (full parity with Debug.js's field list on the PadiHAT side,
 * plus the greenhouse controller's own small fixed set) and plots them on
 * one merged chart, scoped to the same run window the fixed charts use.
 * Defaults to water level + greenhouse temp/humidity ticked (see
 * DEFAULT_GREENHOUSE_FIELDS/DEFAULT_WATER_FIELDS above), overridden by
 * whatever's persisted in localStorage for this controller+water-device
 * pair once the operator has ticked/unticked anything.
 */
export default function AdHocTelemetryPanel({ controllerMac, waterDeviceMac, chartWindow, stepMarkers = [], cameraImages = [] }) {
  const [latestWaterPayload, setLatestWaterPayload] = useState(null);
  // Lazily initialized straight from localStorage (matching useExperiment's
  // own selectedMac pattern), NOT from the hardcoded defaults corrected by
  // a later effect - the first version of this did that, and the "persist
  // on every change" effect below ran in that same initial pass using the
  // still-stale default state, overwriting the real persisted value with
  // the defaults before the "load persisted" effect's setState had even
  // committed. Initializing correctly up front means there's never a wrong
  // value to accidentally persist over the right one.
  const [selectedGreenhouseFields, setSelectedGreenhouseFields] = useState(
    () => loadPersistedFields(controllerMac, waterDeviceMac)?.greenhouseFields ?? DEFAULT_GREENHOUSE_FIELDS
  );
  const [selectedWaterFields, setSelectedWaterFields] = useState(
    () => loadPersistedFields(controllerMac, waterDeviceMac)?.waterFields ?? DEFAULT_WATER_FIELDS
  );
  const [selectedLevelSensorFields, setSelectedLevelSensorFields] = useState(
    () => loadPersistedFields(controllerMac, waterDeviceMac)?.levelSensorFields ?? DEFAULT_LEVEL_SENSOR_FIELDS
  );
  const [selectedCameraFields, setSelectedCameraFields] = useState(
    () => loadPersistedFields(controllerMac, waterDeviceMac)?.cameraFields ?? DEFAULT_CAMERA_FIELDS
  );
  const [data, setData] = useStableState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Field discovery: the SET of fields a device publishes is structural,
  // not scoped to one run, so this only depends on which water device is
  // selected - independent of chartWindow.
  useEffect(() => {
    if (!waterDeviceMac) {
      setLatestWaterPayload(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getTelemetry(waterDeviceMac, 1);
        if (!cancelled) setLatestWaterPayload(res.readings?.[0]?.raw_payload || null);
      } catch {
        if (!cancelled) setLatestWaterPayload(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [waterDeviceMac]);

  const waterNumericFields = useMemo(() => deriveNumericFields(latestWaterPayload), [latestWaterPayload]);

  const waterFieldGroups = useMemo(() => {
    const available = new Set(waterNumericFields);
    const seen = new Set();
    const groups = TELEMETRY_GROUPS
      .map((g) => {
        // d1_raw_us is an array, not a single plottable numeric series -
        // Debug.js special-cases it into a raw-sample overlay; out of scope
        // for this simple ad-hoc line chart.
        const keys = g.keys.filter((k) => available.has(k) && k !== "d1_raw_us");
        keys.forEach((k) => seen.add(k));
        return { title: g.title, keys };
      })
      .filter((g) => g.keys.length > 0);
    const other = waterNumericFields.filter((k) => !seen.has(k));
    if (other.length) groups.push({ title: "Other", keys: other });
    const levelKeys = available.has("d1_est") ? [WATER_LEVEL_FIELD, WATER_LEVEL_EST_FIELD] : [WATER_LEVEL_FIELD];
    return waterDeviceMac ? [{ title: "Water level", keys: levelKeys }, ...groups] : groups;
  }, [waterNumericFields, waterDeviceMac]);

  // Reload the ticked selection (persisted, or the default) whenever the
  // device pair changes, so a stale selection from a different controller/
  // water-device pair never silently carries over. Deliberately NOT keyed
  // on chartWindow.runId - unlike Phase 1, ticks are now scoped to the
  // device pair, not the run, and persist across run changes (the run
  // change itself just re-fetches the same selection for the new window).
  useEffect(() => {
    const persisted = loadPersistedFields(controllerMac, waterDeviceMac);
    setSelectedGreenhouseFields(persisted ? persisted.greenhouseFields : DEFAULT_GREENHOUSE_FIELDS);
    setSelectedWaterFields(persisted ? persisted.waterFields : DEFAULT_WATER_FIELDS);
    setSelectedLevelSensorFields(persisted ? persisted.levelSensorFields : DEFAULT_LEVEL_SENSOR_FIELDS);
    setSelectedCameraFields(persisted ? persisted.cameraFields : DEFAULT_CAMERA_FIELDS);
  }, [controllerMac, waterDeviceMac]);

  // Persist whatever's actually ticked on every change, so it survives a
  // remount/reload - matching useExperiment's STORAGE_KEY pattern.
  useEffect(() => {
    try {
      localStorage.setItem(
        storageKey(controllerMac, waterDeviceMac),
        JSON.stringify({
          greenhouseFields: selectedGreenhouseFields,
          waterFields: selectedWaterFields,
          levelSensorFields: selectedLevelSensorFields,
          cameraFields: selectedCameraFields,
        })
      );
    } catch {
      // Persistence is a nicety (e.g. private browsing/storage quota can
      // throw) - never block the panel from working over it.
    }
  }, [controllerMac, waterDeviceMac, selectedGreenhouseFields, selectedWaterFields, selectedLevelSensorFields, selectedCameraFields]);

  useEffect(() => {
    if (
      !chartWindow ||
      (selectedGreenhouseFields.length === 0 &&
        selectedWaterFields.length === 0 &&
        selectedLevelSensorFields.length === 0 &&
        selectedCameraFields.length === 0)
    ) {
      setData([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [envResult, waterResult, levelSensorResult] = await Promise.all([
          selectedGreenhouseFields.length && controllerMac
            ? getGreenhouseTelemetrySeries(controllerMac, { runId: chartWindow.runId, limit: 2000 })
            : Promise.resolve({ series: [] }),
          selectedWaterFields.length && waterDeviceMac
            ? getTelemetrySeries(waterDeviceMac, seriesRequestFields(selectedWaterFields), chartWindow.start, chartWindow.end, 2000)
            : Promise.resolve({ data: [] }),
          // 500, not 2000 - see useExperiment.js's identical fetch for why
          // (this sensor's 2s cadence packs far more points per unit time
          // than anything else on this panel, which destabilized Recharts'
          // hover hit-testing at higher point counts).
          selectedLevelSensorFields.length && controllerMac
            ? getGreenhouseLevelSensorSeries(controllerMac, {
                start: chartWindow.start,
                end: chartWindow.end,
                limit: 500,
              })
            : Promise.resolve({ series: [] }),
        ]);
        if (cancelled) return;
        // Same depth_mm -> pressure_level_cm conversion as useExperiment's
        // fixed-chart fetch, so this panel's values match that chart's
        // exactly rather than silently using a different unit.
        const levelSensorSeries = (levelSensorResult.series || []).map((point) => ({
          ...point,
          pressure_level_cm: point.depth_mm != null ? point.depth_mm / 10 : null,
        }));
        const merged = mergeTimeSeries([
          { data: envResult.series || [], fields: selectedGreenhouseFields },
          // Derived fields (true tilt) are computed from their reported sources.
          { data: withDerivedSeries(waterResult.data || [], selectedWaterFields), fields: selectedWaterFields },
          { data: levelSensorSeries, fields: selectedLevelSensorFields },
        ]);
        setData(merged);
      } catch (requestError) {
        if (!cancelled) setError(requestError.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Depend on chartWindow's own primitive fields, not the object
    // reference (useExperiment recomputes a new chartWindow object on every
    // poll) - only refetch when the window actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chartWindow?.runId,
    chartWindow?.start,
    chartWindow?.end,
    controllerMac,
    waterDeviceMac,
    selectedGreenhouseFields,
    selectedWaterFields,
    selectedLevelSensorFields,
  ]);

  const toggleGreenhouseField = (key) => {
    setSelectedGreenhouseFields((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]
    );
  };
  const toggleWaterField = (key) => {
    setSelectedWaterFields((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]
    );
  };
  const toggleCameraField = (key) => {
    setSelectedCameraFields((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]
    );
  };
  const toggleLevelSensorField = (key) => {
    setSelectedLevelSensorFields((prev) =>
      prev.includes(key) ? prev.filter((f) => f !== key) : [...prev, key]
    );
  };

  const selectedFields = [
    ...selectedGreenhouseFields,
    ...selectedWaterFields,
    ...selectedLevelSensorFields,
    ...selectedCameraFields,
  ];
  // Memoised on what is ticked: a new array every render would make the memoised chart redraw each time.
  const lines = useMemo(
    () =>
      [...selectedGreenhouseFields, ...selectedWaterFields, ...selectedLevelSensorFields, ...selectedCameraFields].map(
        (field, index) => ({
          dataKey: field,
          name:
            GREENHOUSE_LABELS[field] ||
            LEVEL_SENSOR_LABELS[field] ||
            CAMERA_LABELS[field] ||
            WATER_LEVEL_LABELS[field] ||
            KEY_LABELS[field] ||
            field,
          color: LINE_COLORS[index % LINE_COLORS.length],
          // The labelled photos are a handful of points, not a dense series: show them.
          ...(CAMERA_FIELDS.includes(field) ? { dot: true } : {}),
        })
      ),
    [selectedGreenhouseFields, selectedWaterFields, selectedLevelSensorFields, selectedCameraFields]
  );

  // The labels come from the run's photos (a separate prop), so they are added here rather than in
  // the fetch effect - a new label must not refetch the telemetry.
  const chartData = useMemo(
    () => (selectedCameraFields.includes("truth_level_cm") ? withTruthRows(data, cameraImages) : data),
    [data, cameraImages, selectedCameraFields]
  );
  const labelledCount = countLabelled(cameraImages);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-900">Ad-hoc telemetry</h3>
      <p className="mt-1 text-xs text-slate-500">
        Pick any field either device reports to plot it over the current run window.
      </p>

      {!chartWindow ? (
        <p className="mt-3 text-sm text-slate-500">Start a run to see data here.</p>
      ) : (
        <>
          <div className="mt-3 space-y-2">
            <div className="flex flex-wrap items-start gap-2">
              <span className="w-32 shrink-0 pt-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                Greenhouse
              </span>
              <div className="flex flex-wrap gap-1">
                {GREENHOUSE_FIELDS.map((key) => {
                  const idx = selectedFields.indexOf(key);
                  return (
                    <FieldChip
                      key={key}
                      label={GREENHOUSE_LABELS[key] || key}
                      active={selectedGreenhouseFields.includes(key)}
                      color={idx >= 0 ? LINE_COLORS[idx % LINE_COLORS.length] : undefined}
                      onClick={() => toggleGreenhouseField(key)}
                    />
                  );
                })}
              </div>
            </div>

            <div className="flex flex-wrap items-start gap-2">
              <span className="w-32 shrink-0 pt-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                Level sensor
              </span>
              <div className="flex flex-wrap gap-1">
                {LEVEL_SENSOR_FIELDS.map((key) => {
                  const idx = selectedFields.indexOf(key);
                  return (
                    <FieldChip
                      key={key}
                      label={LEVEL_SENSOR_LABELS[key] || key}
                      active={selectedLevelSensorFields.includes(key)}
                      color={idx >= 0 ? LINE_COLORS[idx % LINE_COLORS.length] : undefined}
                      onClick={() => toggleLevelSensorField(key)}
                    />
                  );
                })}
              </div>
            </div>

            <div className="flex flex-wrap items-start gap-2">
              <span className="w-32 shrink-0 pt-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                Camera
              </span>
              <div className="flex flex-wrap items-center gap-1">
                {CAMERA_FIELDS.map((key) => {
                  const idx = selectedFields.indexOf(key);
                  return (
                    <FieldChip
                      key={key}
                      label={CAMERA_LABELS[key] || key}
                      active={selectedCameraFields.includes(key)}
                      color={idx >= 0 ? LINE_COLORS[idx % LINE_COLORS.length] : undefined}
                      onClick={() => toggleCameraField(key)}
                    />
                  );
                })}
                {labelledCount === 0 && (
                  <span className="text-[11px] text-slate-400">(no photo labelled yet)</span>
                )}
              </div>
            </div>

            {waterFieldGroups.length === 0 ? (
              <p className="text-xs text-slate-400">
                No PadiHAT telemetry fields available yet — select a water-level device with data.
              </p>
            ) : (
              waterFieldGroups.map((group) => (
                <div key={group.title} className="flex flex-wrap items-start gap-2">
                  <span className="w-32 shrink-0 pt-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                    {group.title}
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {group.keys.map((key) => {
                      const idx = selectedFields.indexOf(key);
                      return (
                        <FieldChip
                          key={key}
                          label={WATER_LEVEL_LABELS[key] || KEY_LABELS[key] || key}
                          active={selectedWaterFields.includes(key)}
                          color={idx >= 0 ? LINE_COLORS[idx % LINE_COLORS.length] : undefined}
                          onClick={() => toggleWaterField(key)}
                        />
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="mt-4">
            {error && <p className="text-sm text-red-700">{error}</p>}
            {selectedFields.length === 0 ? (
              <p className="text-sm text-slate-500">Select at least one field to chart.</p>
            ) : loading && data.length === 0 ? (
              <p className="text-sm text-slate-500">Loading…</p>
            ) : (
              <LineChartCard
                title="Ad-hoc selection"
                historicalData={chartData}
                lines={lines}
                connectNulls
                stepMarkers={stepMarkers}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
